import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import AppModal from "../../Components/Accounting/AppModal";
import DataTable from "../../Components/Accounting/DataTable";
import ExportBar from "../../Components/Accounting/ExportBar";
import Select from "react-select";
import ajaxAccounting, { ajaxPayroll } from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const PORTAL = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
  styles: { menuPortal: (b) => ({ ...b, zIndex: 2000 }) },
};

const STATUS_FILTERS = [
  { value: "open", label: "Outstanding" },
  { value: "settled", label: "Settled" },
  { value: "cancelled", label: "Cancelled" },
];

/**
 * Money paid to staff ahead of payday, and how it is being clawed back.
 *
 * An advance is a receivable, not a cost — the organisation is owed it back —
 * so this page is about what is still outstanding, not what was spent. Recovery
 * is automatic: each payroll run deducts the instalment, so the figures here
 * move without anyone keying them in.
 */
const StaffAdvances = () => {
  const navigate = useNavigate();
  const [advances, setAdvances] = useState([]);
  const [outstanding, setOutstanding] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [fyList, setFyList] = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showIssue, setShowIssue] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    employee_id: null, amount: "", recovery_per_period: "",
    issue_date: new Date().toISOString().split("T")[0],
    paid_from_account_id: null, notes: "",
  });

  useEffect(() => { fetchMeta(); }, []);
  useEffect(() => { fetchAdvances(); }, [statusFilter]); // eslint-disable-line

  const fetchMeta = async () => {
    setLoading(true);
    const [fy, emp, acc, out] = await Promise.all([
      ajaxAccounting.listFinancialYears({}),
      ajaxPayroll.listEmployees({}),
      ajaxAccounting.listAccounts({ type: "asset", postable_only: 1 }),
      ajaxPayroll.outstandingAdvances({}),
    ]);
    if (fy.status === "OK") {
      const years = fy.details || [];
      setFyList(years);
      const cur = years.find((y) => y.is_current === 1) || years[0];
      if (cur) setSelectedFY({ value: cur.id, label: cur.name });
    }
    if (emp.status === "OK") setEmployees(Array.isArray(emp.details) ? emp.details : emp.details?.list || []);
    if (acc.status === "OK") setAccounts(acc.details || []);
    if (out.status === "OK") setOutstanding(out.details);
    await fetchAdvances();
    setLoading(false);
  };

  const fetchAdvances = async () => {
    const res = await ajaxPayroll.listAdvances({ status: statusFilter?.value || undefined });
    if (res.status === "OK") setAdvances(res.details || []);
  };

  const handleIssue = async (e) => {
    e.preventDefault();
    if (!form.employee_id) { toast.error("Choose the employee"); return; }
    if (!(parseFloat(form.amount) > 0)) { toast.error("Enter the amount advanced"); return; }
    if (!selectedFY) { toast.error("Select a financial year"); return; }

    setSaving(true);
    const res = await ajaxPayroll.issueAdvance({
      employee_id: form.employee_id.value,
      amount: parseFloat(form.amount),
      issue_date: form.issue_date,
      financial_year_id: selectedFY.value,
      recovery_per_period: parseFloat(form.recovery_per_period) || 0,
      paid_from_account_id: form.paid_from_account_id?.value || null,
      notes: form.notes,
    });
    setSaving(false);

    if (res.status === "OK") {
      toast.success(res.message || "Advance issued");
      setShowIssue(false);
      setForm({
        employee_id: null, amount: "", recovery_per_period: "",
        issue_date: new Date().toISOString().split("T")[0],
        paid_from_account_id: null, notes: "",
      });
      fetchMeta();
    } else toast.error(res.message);
  };

  const openDetail = async (row) => {
    const res = await ajaxPayroll.getAdvance({ id: row.id });
    setDetail(res.status === "OK" ? res.details : { ...row, recoveries: [] });
  };

  const cancel = async (row) => {
    if (!window.confirm(`Cancel the advance to ${row.full_name}? Nothing recovered so far is undone.`)) return;
    const res = await ajaxPayroll.cancelAdvance({ id: row.id });
    if (res.status === "OK") { toast.success(res.message || "Advance cancelled"); fetchMeta(); }
    else toast.error(res.message);
  };

  const employeeOptions = employees.map((e) => ({
    value: e.id, label: `${e.employee_number || ""} ${e.full_name}`.trim(),
  }));
  const accountOptions = accounts.map((a) => ({ value: a.id, label: `${a.account_code} — ${a.name}` }));
  const fyOptions = fyList.map((y) => ({ value: y.id, label: y.name }));

  const columns = [
    { key: "reference", label: "Reference", render: (v) => <code className="tx-12">{v}</code> },
    { key: "full_name", label: "Employee", render: (v, r) => (
        <>
          <strong>{v}</strong>
          {r.position && <div className="tx-11 tx-muted">{r.position}</div>}
        </>
      ) },
    { key: "amount", label: "Advanced", numeric: true, render: (v) => functions.formatCurrency(v) },
    { key: "recovered", label: "Recovered", numeric: true,
      render: (v) => <span className="tx-success">{functions.formatCurrency(v)}</span> },
    { key: "balance", label: "Still Owed", numeric: true,
      render: (v) => <strong className={parseFloat(v) > 0 ? "tx-danger" : "tx-muted"}>{functions.formatCurrency(v)}</strong> },
    { key: "recovery_per_period", label: "Per Run", numeric: true,
      render: (v) => parseFloat(v) > 0 ? functions.formatCurrency(v) : <span className="tx-muted">—</span> },
    { key: "issue_date", label: "Issued", render: (v) => functions.formatDate(v) },
    { key: "status", label: "Status", render: (v) => (
        <span className={`badge ${v === "settled" ? "bg-success" : v === "cancelled" ? "bg-secondary" : "bg-primary"}`}>
          {v}
        </span>
      ) },
    { key: "id", label: "Actions", render: (id, row) => (
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-light" title="View recoveries"
            onClick={(e) => { e.stopPropagation(); openDetail(row); }}>
            <i className="fa-solid fa-list" />
          </button>
          {row.status === "open" && (
            <button className="btn btn-sm btn-light tx-danger" title="Cancel advance"
              onClick={(e) => { e.stopPropagation(); cancel(row); }}>
              <i className="fa-solid fa-ban" />
            </button>
          )}
        </div>
      ) },
  ];

  return (
    <AppContainer
      title="Staff Advances"
      previousPageTitle="Payroll"
      description="Money paid to staff ahead of payday. Each payroll run deducts the agreed instalment automatically until the advance is cleared."
      actions={[
        { label: "Issue Advance", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => setShowIssue(true) },
        { label: "Payroll", icon: "fa-solid fa-money-check-dollar", className: "btn btn-outline-secondary",
          onClick: () => { navigate("/accounting/payroll"); } },
      ]}
    >
      <Toaster />

      <div className="row g-2 mb-3">
        <div className="col-lg-3 col-6">
          <div className="card custom-card p-3 h-100">
            <div className="tx-11 tx-muted text-uppercase">Outstanding</div>
            <div className="fw-bold tx-danger tx-16">
              {functions.formatCurrency(outstanding?.total || 0)}
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-6">
          <div className="card custom-card p-3 h-100">
            <div className="tx-11 tx-muted text-uppercase">Staff with advances</div>
            <div className="fw-bold tx-16">{outstanding?.employees?.length || 0}</div>
          </div>
        </div>
        <div className="col-lg-3 col-6">
          <label className="form-label tx-12 fw-semibold">Financial Year</label>
          <Select options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select year" {...PORTAL} />
        </div>
        <div className="col-lg-3 col-6">
          <label className="form-label tx-12 fw-semibold">Show</label>
          <Select options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter}
            placeholder="All" isClearable {...PORTAL} />
        </div>
      </div>

      <div className="card custom-card p-3">
        <ExportBar
          data={advances}
          columns={[
            { key: "reference", label: "Reference" }, { key: "full_name", label: "Employee" },
            { key: "amount", label: "Advanced" }, { key: "recovered", label: "Recovered" },
            { key: "balance", label: "Still Owed" }, { key: "status", label: "Status" },
          ]}
          filename="staff-advances" pdfTitle="Staff Advances"
          printCode="ACC-PAYROLL" exportCode="ACC-PAYROLL"
        />
        <DataTable
          columns={columns}
          data={advances}
          loading={loading}
          pageSize={15}
          showRowNumbers
          onRowClick={openDetail}
          rowDetailLabel="View recoveries"
          emptyMessage="No advances. Issue one and it will be recovered automatically on the next payroll run."
        />
      </div>

      {/* ISSUE */}
      {showIssue && (
        <AppModal
          title="Issue a staff advance"
          icon="fa-solid fa-hand-holding-dollar"
          subtitle="Recorded as money owed back, not as an expense."
          onClose={() => setShowIssue(false)}
          onSubmit={handleIssue}
          busy={saving}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setShowIssue(false)} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Issue Advance"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Employee *</label>
              <Select options={employeeOptions} value={form.employee_id}
                onChange={(v) => setForm({ ...form, employee_id: v })} placeholder="Select employee" {...PORTAL} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Amount *</label>
              <input type="number" min="0" step="0.01" className="form-control" placeholder="0.00"
                value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Recover per payroll run</label>
              <input type="number" min="0" step="0.01" className="form-control" placeholder="Leave blank to recover in one go"
                value={form.recovery_per_period}
                onChange={(e) => setForm({ ...form, recovery_per_period: e.target.value })} />
              <small className="tx-muted tx-11">Deducted automatically until the advance clears.</small>
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Date issued</label>
              <input type="date" className="form-control" value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Paid from</label>
              <Select options={accountOptions} value={form.paid_from_account_id}
                onChange={(v) => setForm({ ...form, paid_from_account_id: v })}
                placeholder="Default bank account" isClearable {...PORTAL} />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Notes</label>
              <input className="form-control" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          {parseFloat(form.amount) > 0 && parseFloat(form.recovery_per_period) > 0 && (
            <div className="alert alert-light border py-2 tx-13 mt-3 mb-0">
              Cleared in about{" "}
              <strong>{Math.ceil(parseFloat(form.amount) / parseFloat(form.recovery_per_period))}</strong>{" "}
              payroll run(s).
            </div>
          )}
        </AppModal>
      )}

      {/* RECOVERY HISTORY */}
      {detail && (
        <AppModal
          title={`${detail.full_name || "Advance"} — recoveries`}
          icon="fa-solid fa-list"
          subtitle={`${functions.formatCurrency(detail.recovered || 0)} recovered of ${functions.formatCurrency(detail.amount || 0)}`}
          onClose={() => setDetail(null)}
          footer={<button type="button" className="btn btn-primary" onClick={() => setDetail(null)}>Close</button>}
        >
          {(detail.recoveries || []).length === 0 ? (
            <div className="tx-muted tx-13">
              Nothing recovered yet. The next payroll run will deduct the instalment.
            </div>
          ) : (
            <table className="table table-sm mb-0">
              <thead>
                <tr><th>Date</th><th>Payroll run</th><th className="text-end">Recovered</th></tr>
              </thead>
              <tbody>
                {detail.recoveries.map((r) => (
                  <tr key={r.id}>
                    <td>{functions.formatDate(r.recovered_on || r.created_at)}</td>
                    <td>{r.payroll_run_id ? `Run #${r.payroll_run_id}` : "—"}</td>
                    <td className="text-end">{functions.formatCurrency(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </AppModal>
      )}
    </AppContainer>
  );
};

export default StaffAdvances;
